/**
 * @fileoverview Shared contact adapters — the SINGLE source of truth for
 *   mapping raw Dexie contact documents to the PanelContact shape used by
 *   ContactsPanel and related components, and for extracting linked contact
 *   IDs from an entity document.
 * @module ui-admin/components/contactAdapters
 *
 * Responsibilities:
 * - rawContactToPanelContact: Dexie contact doc → PanelContact. Replaces the
 *   previously duplicated `contactToPanelContact` (tenant-web) and
 *   `rawToPanelContact` (mobile-pwa) with one shared implementation so there
 *   is no drift between hosts.
 * - extractLinkedContactIds: Pure function that extracts linked contact IDs
 *   from an entity document (from both `contactIds[]` and
 *   `contacts[].contactId`), filtering to valid string keys. Replaces the
 *   inline extraction logic that was duplicated in tenant-web's
 *   `resolveEntityContactPool` and mobile-pwa's `ParcelContactsTab`.
 *
 * Dependencies:
 * - @firesquirrel/core-domain (resolveLookupDisplayValue, PhoneEntry, EmailEntry,
 *   PhoneType, EmailType, PostalAddress)
 * - ./ContactsPanel (PanelContact)
 *
 * Architecture:
 * - Pure functions; no side effects, no I/O, no Dexie access.
 * - Host-specific DB access (getTenantDb / mobile db) is NOT here — callers
 *   fetch raw docs from Dexie and pass them through these pure transforms.
 *   This follows the DRY injection seam: the package never imports host
 *   internals.
 *
 * Notes:
 * - Tolerates BOTH the canonical Contact shape (displayName, firstName,
 *   lastName, structured phoneNumbers[]/emails[], defaultRoles[],
 *   mailingAddress) and older flat/legacy shapes (`name`, `phone`, `email`,
 *   `emailAddresses[]`, `role`, `contactType`) for cross-platform
 *   compatibility with records synced from different sources.
 * - `mailingAddress` (canonical, possibly USPS-validated PostalAddress) is
 *   passed through so the edit form re-hydrates a previously validated
 *   address instead of starting blank.
 * - The `address` legacy flat string is also passed through so older records
 *   that only have the freeform string still display.
 */

import type {
  PhoneEntry,
  EmailEntry,
  PhoneType,
  EmailType,
  PostalAddress,
} from '@firesquirrel/core-domain';
import { resolveLookupDisplayValue, PHONE_TYPES, EMAIL_TYPES } from '@firesquirrel/core-domain';
import type { PanelContact } from './ContactsPanel';

// ---------------------------------------------------------------------------
// Raw contact document shape (loosely typed — exact fields vary by sync origin)
// ---------------------------------------------------------------------------

/**
 * @contract RawContactDoc
 * Purpose: Loosely-typed shape representing a raw Dexie contact document.
 *   Covers both the canonical Contact shape (tenant-web) and the broader
 *   RawMobileContact shape (mobile-pwa) so the shared adapter handles both.
 * Fields: See inline — all fields optional except _id.
 * Rules: The index signature `[key: string]: unknown` allows sync metadata
 *   (_rev, _deleted, _tenant, etc.) to pass through without explicit typing.
 * Sync: Read from Dexie; written by sync or host services.
 */
export interface RawContactDoc {
  _id: string;
  name?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  affiliations?: { organizationName?: string; title?: string }[];
  phone?: string;
  phoneNumbers?: { number?: string; type?: unknown; isPrimary?: boolean }[];
  email?: string;
  emails?: { email?: string; type?: unknown; isPrimary?: boolean }[];
  emailAddresses?: { address?: string; type?: unknown }[];
  role?: string;
  contactType?: string;
  defaultRoles?: string[];
  address?: string;
  mailingAddress?: PostalAddress;
  isPrimary?: boolean;
  notes?: string;
  isOccupant?: boolean;
  isEmergency?: boolean;
  shouldReceiveReports?: boolean;
  _deleted?: boolean;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Type coercion helpers
// ---------------------------------------------------------------------------

const PHONE_TYPES_SET = new Set<string>(PHONE_TYPES);
const EMAIL_TYPES_SET = new Set<string>(EMAIL_TYPES);

function toPhoneType(v: unknown): PhoneType {
  const s = resolveLookupDisplayValue(v, true);
  return PHONE_TYPES_SET.has(s) ? (s as PhoneType) : 'Office';
}

function toEmailType(v: unknown): EmailType {
  const s = resolveLookupDisplayValue(v, true);
  return EMAIL_TYPES_SET.has(s) ? (s as EmailType) : 'Business';
}

/**
 * @reusable formatMailingAddressLine
 * Usage: Derive the legacy display string consumed by ContactsPanel from a
 *        canonical structured mailing address.
 * Rules: Presentation-only; pure. Prefer a formatted USPS value, then build a
 *        readable single-line address from the structured fields.
 */
function formatMailingAddressLine(address?: PostalAddress): string {
  if (!address) return '';
  if (address.formattedAddress) return address.formattedAddress.replace(/\n/g, ', ');
  const locality = [address.city, address.state].filter(Boolean).join(', ');
  const postalLine = [locality, address.postalCode].filter(Boolean).join(' ');
  return [address.line1, address.line2, postalLine].filter(Boolean).join(', ');
}

// ---------------------------------------------------------------------------
// rawContactToPanelContact
// ---------------------------------------------------------------------------

/**
 * @process rawContactToPanelContact
 *
 * Purpose: The SINGLE shared adapter that maps a raw Dexie contact document
 *   to the PanelContact shape used by ContactsPanel, ContactSearchTypeahead,
 *   and all contact display surfaces. Replaces the previously duplicated
 *   `contactToPanelContact` (tenant-web) and `rawToPanelContact` (mobile-pwa).
 *
 * Steps:
 * 1. Resolve display name from displayName → name → firstName+lastName.
 * 2. Map phoneNumbers[] (or legacy `phone` fallback) to PhoneEntry[].
 * 3. Map emails[] / emailAddresses[] (or legacy `email` fallback) to EmailEntry[].
 * 4. Resolve defaultRoles from defaultRoles[] → role/contactType fallbacks.
 * 5. Pass through mailingAddress (canonical PostalAddress) and legacy address.
 *
 * Inputs: RawContactDoc — a raw Dexie contact document (canonical or legacy).
 * Outputs: PanelContact — for ContactsPanel / ContactSearchTypeahead display.
 * Failure Handling: Each field individually falls back to safe defaults.
 * Idempotency: REQUIRED — pure function, same input → same output.
 *
 * @reusable
 * Usage: Called by both tenant-web and mobile-pwa to convert Dexie contact
 *   docs for display. Hosts fetch raw docs from their respective Dexie
 *   instances and pass them through this shared transform.
 * Rules: Presentation-only pure transform; never mutates the input.
 */
export function rawContactToPanelContact(c: RawContactDoc): PanelContact {
  const name = c.displayName?.trim()
    || c.name?.trim()
    || ([c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed');

  const phones: PhoneEntry[] = c.phoneNumbers?.length
    ? c.phoneNumbers.map((p, i) => {
        const rawType = resolveLookupDisplayValue(p.type, true);
        // Legacy normalization: a type of 'primary' was used in older data as
        // a marker for the primary phone/email. Coerce to the default type and
        // force isPrimary true so the UI highlights it correctly.
        const isLegacyPrimary = rawType === 'primary';
        return {
          _id: `${c._id}-ph-${i}`,
          type: toPhoneType(p.type),
          number: p.number ?? '',
          extension: (p as { extension?: string }).extension,
          isPrimary: isLegacyPrimary || p.isPrimary === true || (p.isPrimary == null && i === 0),
        };
      })
    : c.phone
      ? [{ _id: `${c._id}-ph-0`, type: 'Office' as PhoneType, number: c.phone, isPrimary: true }]
      : [];

  const emails: EmailEntry[] = c.emails?.length
    ? c.emails.map((e, i) => {
        const rawType = resolveLookupDisplayValue(e.type, true);
        const isLegacyPrimary = rawType === 'primary';
        return {
          _id: `${c._id}-em-${i}`,
          type: toEmailType(e.type),
          email: e.email ?? '',
          isPrimary: isLegacyPrimary || e.isPrimary === true || (e.isPrimary == null && i === 0),
        };
      })
    : c.emailAddresses?.length
      ? c.emailAddresses.map((e, i) => ({
          _id: `${c._id}-ea-${i}`,
          type: 'Business' as EmailType,
          email: (e.address ?? ''),
          isPrimary: i === 0,
        }))
      : c.email
        ? [{ _id: `${c._id}-em-0`, type: 'Business' as EmailType, email: c.email, isPrimary: true }]
        : [];

  const defaultRoles = (c.defaultRoles || []).map((r: any) => resolveLookupDisplayValue(r)).filter(Boolean);
  const fallbackRoles = [resolveLookupDisplayValue(c.role), resolveLookupDisplayValue(c.contactType)].filter(Boolean) as string[];
  const finalRoles = defaultRoles.length > 0 ? defaultRoles : fallbackRoles;

  return {
    _id: c._id,
    name,
    firstName: c.firstName,
    lastName: c.lastName,
    company: c.company ?? c.affiliations?.[0]?.organizationName,
    title: c.title ?? c.affiliations?.[0]?.title,
    phoneNumbers: phones,
    emails,
    defaultRoles: finalRoles,
    isPrimary: c.isPrimary,
    isOccupant: c.isOccupant ?? finalRoles.includes('occupant'),
    isEmergency: c.isEmergency ?? finalRoles.includes('emergency-contact'),
    shouldReceiveReports: c.shouldReceiveReports ?? true,
    notes: c.notes,
    // ContactsPanel renders the legacy display field. Derive it from the
    // canonical address so structured-only contacts still show their address.
    address: typeof c.address === 'string' && c.address.trim()
      ? c.address
      : formatMailingAddressLine(c.mailingAddress),
    // Pass through the canonical structured address so the edit form
    // re-hydrates a previously validated mailing address instead of blank.
    mailingAddress: c.mailingAddress,
  };
}

// ---------------------------------------------------------------------------
// extractLinkedContactIds
// ---------------------------------------------------------------------------

/**
 * @contract EntityContactRef
 * Purpose: Minimal shape of an entity's embedded contact reference.
 * Fields: contactId — the linked contact's _id.
 * Rules: Only `contactId` is read; all other fields are ignored.
 * Sync: Embedded in entity.documents.contacts[]; may be stale.
 */
interface EntityContactRef {
  contactId?: unknown;
}

/**
 * @process extractLinkedContactIds
 *
 * Purpose: The SINGLE shared pure function that extracts linked contact IDs
 *   from an entity document. Reads from both `contactIds[]` (the ID-only
 *   array) and `contacts[].contactId` (the embedded reference array),
 *   deduplicates, and filters to valid non-empty string keys.
 *
 * Steps:
 * 1. Collect IDs from `entity.contactIds[]` (if present).
 * 2. Collect IDs from `entity.contacts[].contactId` (if present).
 * 3. Deduplicate via Set.
 * 4. Filter to valid string keys (non-empty). This defends against malformed
 *    synced/imported data where `contactId` can be an array, object, number,
 *    or null — passing a non-string to Dexie's `.get()`/`.bulkGet()` throws.
 *
 * Inputs: A raw entity document (loosely typed).
 * Outputs: string[] — deduplicated, valid contact IDs.
 * Failure Handling: Missing/malformed fields are silently skipped; never throws.
 * Idempotency: REQUIRED — pure function, same input → same output.
 *
 * @reusable
 * Usage: Called by both tenant-web's `resolveEntityContactPool` and
 *   mobile-pwa's contact resolution logic to get the linked IDs before
 *   looking up the full contact records in Dexie.
 * Rules: Pure function; no I/O, no mutations.
 */
export function extractLinkedContactIds(entity: Record<string, unknown>): string[] {
  const rawIds: unknown[] = [
    ...((entity.contactIds as unknown[] | undefined) ?? []),
    ...((entity.contacts as EntityContactRef[] | undefined) ?? [])
      .map((c) => c?.contactId)
      .filter((id) => id != null),
  ];
  const ids: string[] = [];
  for (const id of rawIds) {
    if (typeof id === 'string' && id.length > 0) {
      ids.push(id);
      continue;
    }
    if (id != null && typeof id === 'object' && !Array.isArray(id)) {
      const hex = typeof (id as { toHexString?: () => string }).toHexString === 'function'
        ? (id as { toHexString: () => string }).toHexString()
        : '';
      const text = hex || String(id);
      if (/^[a-fA-F0-9]{24}$/.test(text)) ids.push(text);
    }
  }
  return [...new Set(ids)];
}
